import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SelectInput, useEntityList } from "@pericles/framework-frontend";

import {
  approveRoadmapBlueprint,
  downloadRoadmapSeedPack,
  fetchRoadmapBlueprint,
  exportRoadmapPrioritizedPdf,
  fetchRoadmapPrioritizedRoadmap,
  fetchRoadmapProjects,
  generateRoadmapBlueprint,
  handoffRoadmapToForge,
  isMigrationsRequiredError,
  type RoadmapPrioritizedFactor,
  type RoadmapPrioritizedApplication,
  type RoadmapProject,
  type RoadmapApplicationBlueprint,
  type RoadmapBlueprintStatus,
  type RoadmapMigrationsRequiredDetails,
  updateRoadmapBlueprint,
} from "../api/roadmapClient";

import { Badge, EmptyState, Pane, PaneHeader, Skeleton, Tabs } from "./roadmap/DesignSystem";
import { AtlasMigrationsRequiredCallout } from "./roadmap/AtlasMigrationsRequiredCallout";

type CustomerListItem = { id: string | number; name?: string };

function formatScore(score: number | null): string {
  if (score == null || Number.isNaN(score)) return "—";
  return score.toFixed(2);
}

function resolveBlueprintBadge(status: RoadmapBlueprintStatus | null | undefined): {
  label: string;
  tone: "neutral" | "success" | "warning";
} {
  if (status === "APPROVED") {
    return { label: "APPROVED", tone: "success" };
  }
  if (status === "IN_REVIEW" || status === "NEEDS_APPROVAL") {
    return { label: "NEEDS APPROVAL", tone: "warning" };
  }
  if (status === "READY") {
    return { label: "READY", tone: "neutral" };
  }
  return { label: status ?? "DRAFT", tone: "neutral" };
}

export function AiRoadmapPrioritizedScreen(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<RoadmapProject[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeCustomer") ?? "";
  });
  const [selectedProjectId, setSelectedProjectId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeProject") ?? "";
  });
  const [factors, setFactors] = useState<RoadmapPrioritizedFactor[]>([]);
  const [applications, setApplications] = useState<RoadmapPrioritizedApplication[]>([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [blueprint, setBlueprint] = useState<RoadmapApplicationBlueprint | null>(null);
  const [blueprintJsonInput, setBlueprintJsonInput] = useState("");
  const [blueprintMarkdownInput, setBlueprintMarkdownInput] = useState("");
  const [blueprintTab, setBlueprintTab] = useState("json");
  const [blueprintLoading, setBlueprintLoading] = useState(false);
  const [blueprintError, setBlueprintError] = useState<string | null>(null);
  const [blueprintSaving, setBlueprintSaving] = useState(false);
  const [blueprintGenerating, setBlueprintGenerating] = useState(false);
  const [blueprintApproving, setBlueprintApproving] = useState(false);
  const [seedPackDownloading, setSeedPackDownloading] = useState(false);
  const [handoffLoading, setHandoffLoading] = useState(false);
  const [handoffError, setHandoffError] = useState<string | null>(null);
  const [handoffResult, setHandoffResult] = useState<{ forgeProjectId: string; seedPackHash: string } | null>(null);
  const [migrationError, setMigrationError] = useState<RoadmapMigrationsRequiredDetails | null>(null);

  const { data: customerOptionsData } = useEntityList<CustomerListItem>({
    entity: "Customer",
    page: 0,
    pageSize: 1000,
  });

  const customerOptions = useMemo(() => {
    const options = (customerOptionsData ?? []).map((customer) => ({
      value: String(customer.id),
      label: customer.name ?? String(customer.id),
    }));
    if (selectedCustomerId && !options.find((option) => option.value === selectedCustomerId)) {
      options.push({
        value: selectedCustomerId,
        label: "Client selectionne",
      });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [customerOptionsData, selectedCustomerId]);

  const projectOptions = useMemo(() => {
    const options = projects.map((project) => ({ value: project.id, label: project.name }));
    if (selectedProjectId && !options.find((option) => option.value === selectedProjectId)) {
      options.push({
        value: selectedProjectId,
        label: "Projet selectionne",
      });
    }
    return options;
  }, [projects, selectedProjectId]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.id === selectedProjectId) ?? null,
    [projects, selectedProjectId],
  );

  const canEditBlueprint = selectedProject?.role === "ADMIN" || selectedProject?.role === "EDITOR";
  const canApproveBlueprint = selectedProject?.role === "ADMIN" || selectedProject?.role === "REVIEWER";

  useEffect(() => {
    if (!selectedCustomerId) {
      const storedCustomerId = localStorage.getItem("studio.aiRoadmap.activeCustomer") ?? "";
      if (storedCustomerId) {
        setSelectedCustomerId(storedCustomerId);
      }
    }
    if (!selectedProjectId) {
      const storedProjectId = localStorage.getItem("studio.aiRoadmap.activeProject") ?? "";
      if (storedProjectId) {
        setSelectedProjectId(storedProjectId);
      }
    }
  }, [selectedCustomerId, selectedProjectId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextCustomerId = params.get("customerId")?.trim() ?? "";
    const nextProjectId = params.get("projectId")?.trim() ?? "";

    if (nextCustomerId && nextCustomerId !== selectedCustomerId) {
      setSelectedCustomerId(nextCustomerId);
    }
    if (nextProjectId && nextProjectId !== selectedProjectId) {
      setSelectedProjectId(nextProjectId);
    }
  }, [location.search, selectedCustomerId, selectedProjectId]);

  useEffect(() => {
    if (!selectedCustomerId) {
      setProjects([]);
      setSelectedProjectId("");
      return;
    }
    localStorage.setItem("studio.aiRoadmap.activeCustomer", selectedCustomerId);
    let isActive = true;
    fetchRoadmapProjects(selectedCustomerId)
      .then((loaded) => {
        if (!isActive) return;
        setProjects(loaded);
        if (!loaded.length) {
          setSelectedProjectId("");
          return;
        }
        if (!loaded.find((project) => project.id === selectedProjectId)) {
          setSelectedProjectId(loaded[0].id);
        }
      })
      .catch(() => {
        if (!isActive) return;
        setProjects([]);
        setSelectedProjectId("");
      });
    return () => {
      isActive = false;
    };
  }, [selectedCustomerId, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setFactors([]);
      setApplications([]);
      setSelectedApplicationId("");
      return;
    }
    localStorage.setItem("studio.aiRoadmap.activeProject", selectedProjectId);
    window.dispatchEvent(new Event("roadmap:project-changed"));
    const loadData = async () => {
      setLoading(true);
      setDataError(null);
      setMigrationError(null);
      try {
        const data = await fetchRoadmapPrioritizedRoadmap(selectedProjectId);
        setFactors(data.factors);
        setApplications(data.applications);
        setSelectedApplicationId((current) => {
          if (data.applications.find((application) => application.id === current)) {
            return current;
          }
          return data.applications[0]?.id ?? "";
        });
      } catch (error) {
        if (isMigrationsRequiredError(error)) {
          setMigrationError(error.details);
          setDataError(null);
        } else {
          const message = error instanceof Error ? error.message : "Impossible de charger la roadmap priorisée.";
          setDataError(message);
        }
        setFactors([]);
        setApplications([]);
        setSelectedApplicationId("");
      } finally {
        setLoading(false);
      }
    };
    void loadData();
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedApplicationId) {
      setBlueprint(null);
      setBlueprintJsonInput("");
      setBlueprintMarkdownInput("");
      setBlueprintError(null);
      setHandoffResult(null);
      setHandoffError(null);
      return;
    }
    let isActive = true;
    setBlueprintLoading(true);
    setBlueprintError(null);
    setHandoffResult(null);
    setHandoffError(null);
    fetchRoadmapBlueprint(selectedApplicationId)
      .then((payload) => {
        if (!isActive) return;
        setBlueprint(payload.blueprint);
        setBlueprintJsonInput(payload.blueprint ? JSON.stringify(payload.blueprint.blueprintJson, null, 2) : "");
        setBlueprintMarkdownInput(payload.blueprint?.blueprintMarkdown ?? "");
        setBlueprintTab("json");
      })
      .catch((error) => {
        if (!isActive) return;
        const message = error instanceof Error ? error.message : "Impossible de charger le blueprint.";
        setBlueprintError(message);
        setBlueprint(null);
        setBlueprintJsonInput("");
        setBlueprintMarkdownInput("");
      })
      .finally(() => {
        if (!isActive) return;
        setBlueprintLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [selectedApplicationId]);

  const handleExportPdf = async () => {
    if (!selectedProjectId) return;
    setExporting(true);
    try {
      const blob = await exportRoadmapPrioritizedPdf(selectedProjectId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `prioritized-roadmap-${selectedProjectId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (isMigrationsRequiredError(error)) {
        setMigrationError(error.details);
      } else {
        console.error(error);
      }
    } finally {
      setExporting(false);
    }
  };

  const handleGenerateBlueprint = async () => {
    if (!selectedApplicationId) return;
    setBlueprintGenerating(true);
    setBlueprintError(null);
    try {
      const response = await generateRoadmapBlueprint(selectedApplicationId);
      if (!response.ok && response.reasons?.length) {
        setBlueprintError(response.reasons.map((reason) => reason.message).join(" "));
      }
      setBlueprint(response.blueprint);
      setBlueprintJsonInput(response.blueprint ? JSON.stringify(response.blueprint.blueprintJson, null, 2) : "");
      setBlueprintMarkdownInput(response.blueprint?.blueprintMarkdown ?? "");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible de générer le blueprint.";
      setBlueprintError(message);
    } finally {
      setBlueprintGenerating(false);
    }
  };

  const handleSaveBlueprint = async () => {
    if (!selectedApplicationId) return;
    setBlueprintSaving(true);
    setBlueprintError(null);
    try {
      const parsed = blueprintJsonInput.trim() ? (JSON.parse(blueprintJsonInput) as Record<string, unknown>) : {};
      const response = await updateRoadmapBlueprint(selectedApplicationId, {
        blueprintJson: parsed,
        blueprintMarkdown: blueprintMarkdownInput.trim() ? blueprintMarkdownInput.trim() : null,
      });
      setBlueprint(response.blueprint);
      setBlueprintJsonInput(response.blueprint ? JSON.stringify(response.blueprint.blueprintJson, null, 2) : "");
      setBlueprintMarkdownInput(response.blueprint?.blueprintMarkdown ?? "");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Impossible de sauvegarder le blueprint.";
      setBlueprintError(message);
    } finally {
      setBlueprintSaving(false);
    }
  };

  const handleApproveBlueprint = async () => {
    if (!selectedApplicationId) return;
    setBlueprintApproving(true);
    setBlueprintError(null);
    try {
      const response = await approveRoadmapBlueprint(selectedApplicationId);
      setBlueprint(response.blueprint ?? null);
      if (response.blueprint) {
        setBlueprintJsonInput(JSON.stringify(response.blueprint.blueprintJson, null, 2));
        setBlueprintMarkdownInput(response.blueprint.blueprintMarkdown ?? "");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible d'approuver le blueprint.";
      setBlueprintError(message);
    } finally {
      setBlueprintApproving(false);
    }
  };

  const handleDownloadSeedPack = async () => {
    if (!selectedApplicationId) return;
    setSeedPackDownloading(true);
    setBlueprintError(null);
    try {
      const blob = await downloadRoadmapSeedPack(selectedApplicationId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `seed-pack-${selectedApplicationId}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible de télécharger le seed pack.";
      setBlueprintError(message);
    } finally {
      setSeedPackDownloading(false);
    }
  };

  const handleHandoff = async () => {
    if (!selectedApplicationId) return;
    setHandoffLoading(true);
    setHandoffError(null);
    setHandoffResult(null);
    try {
      const response = await handoffRoadmapToForge(selectedApplicationId);
      if (!response.ok && response.reasons?.length) {
        setHandoffError(response.reasons.map((reason) => reason.message).join(" "));
      }
      if (response.forgeProjectId && response.seedPackHash) {
        setHandoffResult({ forgeProjectId: response.forgeProjectId, seedPackHash: response.seedPackHash });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Impossible de créer le projet Forge.";
      setHandoffError(message);
    } finally {
      setHandoffLoading(false);
    }
  };

  const factorColumns = useMemo(() => {
    return factors.map((factor) => ({
      id: factor.id,
      label: factor.factor,
      weight: factor.weight,
    }));
  }, [factors]);

  const selectedApplication = useMemo(
    () => applications.find((application) => application.id === selectedApplicationId) ?? null,
    [applications, selectedApplicationId],
  );

  const totalUseCaseCount = useMemo(
    () => applications.reduce((sum, application) => sum + application.useCaseCount, 0),
    [applications],
  );

  const sponsorOutputItems = useMemo(() => {
    const projectLink = selectedProjectId ? `/foundry?projectId=${encodeURIComponent(selectedProjectId)}` : null;
    const jarvisLink = selectedProjectId ? `/jarvis?projectId=${encodeURIComponent(selectedProjectId)}` : null;
    return [
      {
        label: "Portfolio truth",
        value: selectedProject ? `${applications.length} applications / ${totalUseCaseCount} use cases` : "No project selected",
      },
      {
        label: "Blueprint status",
        value: resolveBlueprintBadge(blueprint?.status).label,
      },
      {
        label: "Seed pack",
        value: blueprint ? "Ready to export" : "Pending blueprint",
      },
      {
        label: "Forge handoff",
        value: handoffResult ? `Created (${handoffResult.forgeProjectId})` : "Not yet handed off",
      },
      {
        label: "Foundry route",
        value: projectLink ?? "Unavailable",
      },
      {
        label: "Jarvis route",
        value: jarvisLink ?? "Unavailable",
      },
    ];
  }, [applications.length, blueprint, handoffResult, selectedProject, selectedProjectId, totalUseCaseCount]);

  const shouldShowSponsorOutputs = Boolean(selectedProjectId) && !migrationError && !dataError;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px]">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Client</div>
          <SelectInput
            data-testid="atlas-customer-select"
            value={selectedCustomerId}
            onChange={(event) => setSelectedCustomerId(event.target.value)}
          >
            <option value="">Sélectionner</option>
            {customerOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectInput>
        </div>
        <div className="min-w-[260px]">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Projet</div>
          <SelectInput
            data-testid="atlas-project-select"
            value={selectedProjectId}
            onChange={(event) => setSelectedProjectId(event.target.value)}
            disabled={!selectedCustomerId}
          >
            <option value="">Sélectionner</option>
            {projectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectInput>
        </div>
      </div>

      <Pane className="overflow-hidden">
        <PaneHeader
          title="Prioritized Applications"
          meta={
            applications.length ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Badge label={`${applications.length} applications`} />
                <span>{totalUseCaseCount} use cases</span>
                <span>{factors.length} facteurs</span>
              </div>
            ) : null
          }
          actions={
            selectedProjectId ? (
              <button
                type="button"
                onClick={() => void handleExportPdf()}
                disabled={exporting}
                className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
              >
                {exporting ? "Export..." : "Exporter PDF"}
              </button>
            ) : null
          }
        />

        {shouldShowSponsorOutputs ? (
          <div
            className="mx-4 mt-4 rounded-lg border border-border bg-background p-4"
            data-testid="atlas-sponsor-outputs"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-foreground">Atlas sponsor outputs</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Executive-ready package linking prioritized use cases to Foundry build readiness and Jarvis delivery.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => selectedProjectId && navigate(`/foundry?projectId=${encodeURIComponent(selectedProjectId)}`)}
                  disabled={!selectedProjectId}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Open Foundry contract
                </button>
                <button
                  type="button"
                  onClick={() => selectedProjectId && navigate(`/jarvis?projectId=${encodeURIComponent(selectedProjectId)}`)}
                  disabled={!selectedProjectId}
                  className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Open Jarvis delivery
                </button>
              </div>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {sponsorOutputItems.map((item) => (
                <div key={item.label} className="rounded-md border border-border bg-muted/20 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {item.label}
                  </div>
                  <div className="mt-2 text-sm font-medium text-foreground">{item.value}</div>
                </div>
              ))}
            </div>

            {selectedApplication ? (
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div className="rounded-md border border-border bg-background p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Selected application
                  </div>
                  <div className="mt-2 text-sm font-semibold text-foreground">
                    {selectedApplication.name ?? "Application"}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Score {formatScore(selectedApplication.finalScore)} • {selectedApplication.useCaseCount} use cases •{" "}
                    {selectedApplication.entryCount} entries
                  </div>
                </div>
                <div className="rounded-md border border-border bg-background p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Next move
                  </div>
                  <div className="mt-2 text-sm text-foreground">
                    {handoffResult
                      ? "Pilot seed is in Forge. Continue in Foundry to lock the build-ready contract and hand off to Jarvis."
                      : blueprint
                        ? "Blueprint exists. Validate the seed pack and create the Forge project to enter deterministic delivery."
                        : "Generate the application blueprint to turn Atlas scoring into a build-ready payload."}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        {loading ? (
          <Skeleton rows={6} />
        ) : migrationError ? (
          <div className="space-y-4 p-4">
            <AtlasMigrationsRequiredCallout details={migrationError} />
            <EmptyState
              title="Migrations nécessaires"
              description="Appliquez les migrations Prisma pour afficher la roadmap priorisée."
            />
          </div>
        ) : dataError ? (
          <EmptyState title={dataError} description="Réessayez ou changez de projet." />
        ) : !selectedProjectId ? (
          <EmptyState
            title="Sélectionnez un projet"
            description="Choisissez un projet de roadmap pour afficher la priorisation."
          />
        ) : (
          <div className="space-y-4">
            {!shouldShowSponsorOutputs ? (
              <div
                className="rounded-lg border border-border bg-background p-4"
                data-testid="atlas-sponsor-outputs"
              >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-foreground">Atlas sponsor outputs</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Executive-ready package linking prioritized use cases to Foundry build readiness and Jarvis delivery.
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => selectedProjectId && navigate(`/foundry?projectId=${encodeURIComponent(selectedProjectId)}`)}
                    disabled={!selectedProjectId}
                    className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Open Foundry contract
                  </button>
                  <button
                    type="button"
                    onClick={() => selectedProjectId && navigate(`/jarvis?projectId=${encodeURIComponent(selectedProjectId)}`)}
                    disabled={!selectedProjectId}
                    className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Open Jarvis delivery
                  </button>
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {sponsorOutputItems.map((item) => (
                  <div key={item.label} className="rounded-md border border-border bg-muted/20 p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {item.label}
                    </div>
                    <div className="mt-2 text-sm font-medium text-foreground">{item.value}</div>
                  </div>
                ))}
              </div>

              {selectedApplication ? (
                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className="rounded-md border border-border bg-background p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Selected application
                    </div>
                    <div className="mt-2 text-sm font-semibold text-foreground">
                      {selectedApplication.name ?? "Application"}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      Score {formatScore(selectedApplication.finalScore)} • {selectedApplication.useCaseCount} use cases •{" "}
                      {selectedApplication.entryCount} entries
                    </div>
                  </div>
                  <div className="rounded-md border border-border bg-background p-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Next move
                    </div>
                    <div className="mt-2 text-sm text-foreground">
                      {handoffResult
                        ? "Pilot seed is in Forge. Continue in Foundry to lock the build-ready contract and hand off to Jarvis."
                        : blueprint
                          ? "Blueprint exists. Validate the seed pack and create the Forge project to enter deterministic delivery."
                          : "Generate the application blueprint to turn Atlas scoring into a build-ready payload."}
                    </div>
                  </div>
                </div>
              ) : null}
              </div>
            ) : null}

            {applications.length === 0 ? (
              <EmptyState
                title="Aucune application priorisée"
                description="Ajoutez des scores pour calculer la priorisation."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border text-left text-sm">
                  <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Rang</th>
                      <th className="px-4 py-3">Application</th>
                      <th className="px-4 py-3">Use cases</th>
                      <th className="px-4 py-3">Score /100</th>
                      <th className="px-4 py-3">Entrées</th>
                      {factorColumns.map((factor) => (
                        <th key={factor.id} className="px-4 py-3">
                          <div className="font-semibold text-foreground">{factor.label}</div>
                          <div className="text-[11px] text-muted-foreground">Poids {factor.weight}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {applications.map((application, index) => {
                      const scoreByFactor = new Map(
                        application.factorScores.map((score) => [score.factorId, score]),
                      );
                      const isSelected = application.id === selectedApplicationId;
                      return (
                        <tr
                          key={application.id}
                          className={`cursor-pointer hover:bg-muted/30 ${isSelected ? "bg-muted/20" : ""}`}
                          onClick={() => setSelectedApplicationId(application.id)}
                        >
                          <td className="px-4 py-3 text-sm font-semibold text-muted-foreground">
                            {index + 1}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-foreground">
                              {application.name ?? "Application"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {application.useCaseCount}
                          </td>
                          <td className="px-4 py-3 font-semibold text-foreground">
                            {formatScore(application.finalScore)}
                          </td>
                          <td className="px-4 py-3 text-sm text-muted-foreground">
                            {application.entryCount}
                          </td>
                          {factorColumns.map((factor) => {
                            const score = scoreByFactor.get(factor.id);
                            return (
                              <td key={factor.id} className="px-4 py-3 text-sm">
                                <div className="font-semibold text-foreground">
                                  {formatScore(score?.score ?? null)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Pondéré {formatScore(score?.weightedScore ?? null)}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {selectedApplication ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-border bg-background p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-semibold text-foreground">
                        {selectedApplication.name ?? "Application"}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {selectedApplication.useCaseCount} use cases • Score {formatScore(selectedApplication.finalScore)}
                      </div>
                    </div>
                    <Badge label={`${selectedApplication.entryCount} entrées`} />
                  </div>

                  <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
                    <div>
                      <div className="text-xs font-semibold uppercase text-muted-foreground">
                        Use cases inclus
                      </div>
                      <div className="mt-2 space-y-1 text-sm text-foreground">
                        {selectedApplication.useCases.length ? (
                          selectedApplication.useCases.map((useCase) => (
                            <div key={useCase.id} className="flex items-center gap-2">
                              <span className="font-medium">{useCase.name ?? "Use case"}</span>
                              <span className="text-xs text-muted-foreground">({useCase.id})</span>
                            </div>
                          ))
                        ) : (
                          <div className="text-xs text-muted-foreground">Aucun use case affecté.</div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase text-muted-foreground">
                        Détail des facteurs
                      </div>
                      <div className="mt-2 space-y-2">
                        {selectedApplication.factorScores.map((factorScore) => {
                          const factor = factorColumns.find((item) => item.id === factorScore.factorId);
                          return (
                            <div key={factorScore.factorId} className="rounded-md border border-border p-3">
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="text-sm font-semibold text-foreground">
                                  {factor?.label ?? factorScore.factorId}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Score {formatScore(factorScore.score)}
                                </div>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                {factorScore.roleScores.map((roleScore) => (
                                  <Badge
                                    key={roleScore.role}
                                    label={`${roleScore.role}: ${formatScore(roleScore.score)}`}
                                  />
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-border bg-background p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-foreground">Blueprint &amp; Handoff</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Générez le blueprint, validez-le puis exportez le seed pack pour Forge.
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge {...resolveBlueprintBadge(blueprint?.status)} />
                      <button
                        type="button"
                        onClick={() => void handleGenerateBlueprint()}
                        disabled={!canEditBlueprint || blueprintGenerating}
                        className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {blueprintGenerating ? "Génération..." : "Générer le blueprint"}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleApproveBlueprint()}
                        disabled={!canApproveBlueprint || blueprintApproving || !blueprint}
                        className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {blueprintApproving ? "Approbation..." : "Approuver"}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDownloadSeedPack()}
                        disabled={seedPackDownloading}
                        className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {seedPackDownloading ? "Téléchargement..." : "Télécharger le seed pack"}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleHandoff()}
                        disabled={handoffLoading}
                        className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {handoffLoading ? "Création..." : "Créer le projet Forge"}
                      </button>
                    </div>
                  </div>

                  {blueprintLoading ? (
                    <div className="mt-4 text-xs text-muted-foreground">Chargement du blueprint…</div>
                  ) : blueprintError ? (
                    <div className="mt-4 text-xs text-destructive">{blueprintError}</div>
                  ) : !blueprint ? (
                    <div className="mt-4 text-xs text-muted-foreground">Aucun blueprint enregistré.</div>
                  ) : (
                    <div className="mt-4 space-y-3">
                      <Tabs
                        tabs={[
                          { id: "json", label: "JSON" },
                          { id: "markdown", label: "Markdown" },
                        ]}
                        activeId={blueprintTab}
                        onChange={setBlueprintTab}
                      />
                      {blueprintTab === "json" ? (
                        <textarea
                          className="min-h-[240px] w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground"
                          value={blueprintJsonInput}
                          onChange={(event) => setBlueprintJsonInput(event.target.value)}
                          disabled={!canEditBlueprint || blueprintSaving}
                        />
                      ) : (
                        <textarea
                          className="min-h-[240px] w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground"
                          value={blueprintMarkdownInput}
                          onChange={(event) => setBlueprintMarkdownInput(event.target.value)}
                          disabled={!canEditBlueprint || blueprintSaving}
                        />
                      )}
                      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span>Inputs hash: {blueprint.inputsHash}</span>
                        <span>•</span>
                        <span>Mis à jour: {new Date(blueprint.updatedAt).toLocaleString()}</span>
                      </div>
                      <div>
                        <button
                          type="button"
                          onClick={() => void handleSaveBlueprint()}
                          disabled={!canEditBlueprint || blueprintSaving}
                          className="rounded-lg border border-border px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {blueprintSaving ? "Enregistrement..." : "Sauvegarder"}
                        </button>
                      </div>
                    </div>
                  )}

                  {handoffError ? (
                    <div className="mt-4 text-xs text-destructive">{handoffError}</div>
                  ) : null}
                  {handoffResult ? (
                    <div className="mt-4 rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                      Projet Forge créé:{" "}
                      <a className="font-semibold text-foreground underline" href={`/projects/${handoffResult.forgeProjectId}`}>
                        {handoffResult.forgeProjectId}
                      </a>
                      <div>Seed pack hash: {handoffResult.seedPackHash}</div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </Pane>
    </div>
  );
}
